// scheduling/components/distribution/BeforeAfterSummary.jsx
// Visual comparison: Calendar View + Capacity View + Change Summary.
// Fixed weekday columns, stacked histogram bars, day headers on both sections.
//
// Originally lived at pages/EditModalSummary.jsx — moved + renamed so it can be
// reused outside the edit modal (placeholder assign modal, future surfaces).
// Renaming dropped the "EditModal" prefix; nothing about the behavior changed.

import { useMemo } from 'react';

function toISO(d) { return d instanceof Date ? d.toISOString().slice(0, 10) : (d || '').slice(0, 10); }
function r2(x) { return Math.round((x || 0) * 100) / 100; }

function fmtDay(iso) {
  const d = new Date(iso + 'T12:00:00');
  const dow = d.getDay();
  return { day: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dow], date: d.getDate(), month: d.toLocaleDateString('en-US', { month: 'short' }), dow };
}

function isWeekend(iso) { return new Date(iso + 'T12:00:00').getDay() === 0; }
function getMonday(iso) { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return toISO(d); }

const WEEKS_PER_ROW = 3;

export default function BeforeAfterSummary({
  originalDailyHours = {}, proposedDailyHours = {}, getDayCap, ptoByStaffDay = {},
  staffId, originalStaff, newStaff, originalJob, newJob, isNewEntry = false,
  dayHoursByStaff = {}, displayTotal, holidayByDate = {}, globalAssignmentByDate = {}, staffDeptCode, sameJobDailyHours = {},
  rangeDays = [],
}) {
  const staffPTO = ptoByStaffDay[staffId] || {};
  // Days come from the hour maps, plus (optionally) every workday of the entry's range.
  // Capacity-aware distribution skips a day that is already full, so without rangeDays
  // that day simply vanished from both views — a gap that looks like a bug. With it, the
  // day shows "—" for this job and its gray other-jobs bar up to capacity: the reason.
  const allDays = useMemo(() => {
    const set = new Set([...Object.keys(originalDailyHours), ...Object.keys(proposedDailyHours), ...rangeDays]);
    return [...set].filter(d => !isWeekend(d)).sort();
  }, [originalDailyHours, proposedDailyHours, rangeDays]);

  const hasSaturdays = useMemo(() => allDays.some(d => new Date(d + 'T12:00:00').getDay() === 6), [allDays]);
  const daysPerWeek = hasSaturdays ? 6 : 5;

  const dayAnalysis = useMemo(() => allDays.map(iso => {
    const orig = originalDailyHours[iso] || 0;
    const proposed = proposedDailyHours[iso] || 0;
    const cap = getDayCap ? getDayCap(staffId, iso) : 8;
    const hasPTO = (staffPTO[iso] || 0) > 0;
    const holiday = holidayByDate[iso];
    const hasHoliday = !!holiday;
    const globalEntries = globalAssignmentByDate[iso] || [];
    const globalEntry = globalEntries.find(ga => !ga.departments || (staffDeptCode && ga.departments.split(',').includes(staffDeptCode)));
    const hasGlobalAssignment = !!globalEntry;
    const totalStaffDay = dayHoursByStaff[staffId]?.[iso] || 0;
    const globalHours = hasGlobalAssignment ? (globalEntry.hours || 0) : 0;
    // The staffer's existing hours on THIS job (excl. the entry being edited) count as
    // "this job" (green), not "other jobs" (gray) — so scheduling more on a job they
    // already have shows correctly. sameJobDailyHours defaults to {} for other consumers.
    const sameJob = sameJobDailyHours[iso] || 0;
    const otherJobHours = Math.max(0, totalStaffDay - orig - sameJob - globalHours);
    const thisJobBefore = orig + sameJob;
    const thisJobAfter = proposed + sameJob;
    // Diff is computed on THIS-JOB totals (edited block + other same-job entries on the
    // day), so extending/dragging a block onto a day another same-job entry already
    // occupies reads as a merge (e.g. 4 → 6, +2) instead of a spurious "+NEW".
    const isNew = thisJobBefore < 0.01 && thisJobAfter > 0.01;
    const isRemoved = thisJobBefore > 0.01 && thisJobAfter < 0.01;
    const isModified = !isNew && !isRemoved && Math.abs(thisJobBefore - thisJobAfter) > 0.01;
    const delta = thisJobAfter - thisJobBefore;
    return { iso, orig, proposed, cap, hasPTO, hasHoliday, holidayName: holiday?.name, hasGlobalAssignment, globalAssignmentName: globalEntry?.name, globalHours, isNew, isRemoved, isModified, delta, otherJobHours, sameJob, thisJobBefore, thisJobAfter, ...fmtDay(iso) };
  }), [allDays, originalDailyHours, proposedDailyHours, getDayCap, staffId, staffPTO, holidayByDate, globalAssignmentByDate, staffDeptCode, dayHoursByStaff, sameJobDailyHours]);

  // Group into weeks with fixed weekday slots
  const weeks = useMemo(() => {
    const map = {};
    for (const d of dayAnalysis) {
      const mon = getMonday(d.iso);
      if (!map[mon]) map[mon] = { monday: mon, days: {} };
      map[mon].days[d.dow === 6 ? 5 : d.dow - 1] = d;
    }
    return Object.values(map).sort((a, b) => a.monday.localeCompare(b.monday));
  }, [dayAnalysis]);

  const weekRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < weeks.length; i += WEEKS_PER_ROW) rows.push(weeks.slice(i, i + WEEKS_PER_ROW));
    return rows;
  }, [weeks]);

  const maxWeeksInRow = Math.min(weeks.length, WEEKS_PER_ROW);
  const totalCols = maxWeeksInRow * daysPerWeek;
  const gridTemplate = `36px repeat(${totalCols}, 1fr)`;

  const origTotal = Object.values(originalDailyHours).reduce((s, h) => s + (h || 0), 0);
  // Use displayTotal (from modalHours input) to avoid floating point drift from summing rounded dailies
  const proposedTotal = displayTotal != null ? displayTotal : Object.values(proposedDailyHours).reduce((s, h) => s + (h || 0), 0);
  const totalDelta = proposedTotal - origTotal;
  const maxH = Math.max(8, ...dayAnalysis.map(d => Math.max(d.thisJobBefore + d.otherJobHours + (d.globalHours || 0), d.thisJobAfter + d.otherJobHours + (d.globalHours || 0), d.cap)));

  const summaryItems = useMemo(() => {
    const items = [];
    if (originalStaff && newStaff && originalStaff !== newStaff)
      items.push({ icon: '👤', text: `Reassigning from ${originalStaff} → ${newStaff}`, color: '#3B82F6' });
    if (originalJob && newJob && originalJob !== newJob)
      items.push({ icon: '📋', text: `Moving to ${newJob}`, color: '#8B5CF6' });
    if (!isNewEntry && Math.abs(totalDelta) > 0.01)
      items.push({ icon: totalDelta > 0 ? '▲' : '▼', text: `Hours: ${r2(origTotal)}h → ${r2(proposedTotal)}h (${totalDelta > 0 ? '+' : ''}${r2(totalDelta)}h)`, color: totalDelta > 0 ? '#059669' : '#DC2626' });
    const addedDays = dayAnalysis.filter(d => d.isNew);
    const removedDays = dayAnalysis.filter(d => d.isRemoved);
    if (addedDays.length > 0)
      items.push({ icon: '📅', text: `+${addedDays.length} day${addedDays.length > 1 ? 's' : ''} added (${addedDays.map(d => `${d.month} ${d.date}`).join(', ')})`, color: '#F59E0B' });
    if (removedDays.length > 0)
      items.push({ icon: '✂️', text: `${removedDays.length} day${removedDays.length > 1 ? 's' : ''} removed`, color: '#9CA3AF' });
    const ptoDays = dayAnalysis.filter(d => d.hasPTO);
    const ptoReduced = ptoDays.filter(d => d.orig > 0 && d.proposed < d.orig);
    const ptoHrsMoved = ptoReduced.reduce((s, d) => s + (d.orig - d.proposed), 0);
    if (ptoReduced.length > 0 && ptoHrsMoved > 0.01)
      items.push({ icon: '🏖️', text: `${r2(ptoHrsMoved)}h moved off ${ptoReduced.length} PTO day${ptoReduced.length > 1 ? 's' : ''}`, color: '#F59E0B' });
    const ptoConflict = ptoDays.filter(d => d.proposed > 0);
    if (ptoConflict.length > 0 && ptoReduced.length === 0)
      items.push({ icon: '🏖️', text: `${ptoConflict.length} PTO day${ptoConflict.length > 1 ? 's' : ''} with scheduled hours`, color: '#F59E0B' });
    const holidayDays = dayAnalysis.filter(d => d.hasHoliday);
    const holidayReduced = holidayDays.filter(d => d.orig > 0 && d.proposed < d.orig);
    const holidayHrsMoved = holidayReduced.reduce((s, d) => s + (d.orig - d.proposed), 0);
    if (holidayReduced.length > 0 && holidayHrsMoved > 0.01) {
      const names = [...new Set(holidayReduced.map(d => d.holidayName).filter(Boolean))];
      items.push({ icon: '🎄', text: `${r2(holidayHrsMoved)}h moved off ${holidayReduced.length} holiday${holidayReduced.length > 1 ? 's' : ''}${names.length ? ` (${names.join(', ')})` : ''}`, color: '#7C3AED' });
    }
    const holidayConflicts = holidayDays.filter(d => d.proposed > 0);
    if (holidayConflicts.length > 0 && holidayReduced.length === 0) {
      const names = [...new Set(holidayConflicts.map(d => d.holidayName).filter(Boolean))];
      items.push({ icon: '🎄', text: `${holidayConflicts.length} holiday${holidayConflicts.length > 1 ? 's' : ''} with scheduled hours${names.length ? ` (${names.join(', ')})` : ''}`, color: '#7C3AED' });
    }
    const globalDays = dayAnalysis.filter(d => d.hasGlobalAssignment);
    const globalReduced = globalDays.filter(d => d.orig > 0 && d.proposed < d.orig);
    const globalHrsMoved = globalReduced.reduce((s, d) => s + (d.orig - d.proposed), 0);
    if (globalReduced.length > 0 && globalHrsMoved > 0.01) {
      const names = [...new Set(globalReduced.map(d => d.globalAssignmentName).filter(Boolean))];
      items.push({ icon: '📅', text: `${r2(globalHrsMoved)}h moved off ${globalReduced.length} firm-wide event day${globalReduced.length > 1 ? 's' : ''}${names.length ? ` (${names.join(', ')})` : ''}`, color: '#0891B2' });
    }
    const globalConflicts = globalDays.filter(d => d.proposed > 0);
    if (globalConflicts.length > 0 && globalReduced.length === 0) {
      const names = [...new Set(globalConflicts.map(d => d.globalAssignmentName).filter(Boolean))];
      items.push({ icon: '📅', text: `${globalConflicts.length} firm-wide event day${globalConflicts.length > 1 ? 's' : ''} with scheduled hours${names.length ? ` (${names.join(', ')})` : ''}`, color: '#0891B2' });
    }
    return items;
  }, [originalStaff, newStaff, originalJob, newJob, origTotal, proposedTotal, totalDelta, dayAnalysis, isNewEntry]);

  if (allDays.length === 0) return null;

  // Build slot arrays for a row of weeks (padded to maxWeeksInRow * daysPerWeek)
  const buildSlots = (rowWeeks) => {
    const slots = [];
    for (let wi = 0; wi < maxWeeksInRow; wi++) {
      const week = rowWeeks[wi];
      for (let di = 0; di < daysPerWeek; di++) {
        slots.push(week ? (week.days[di] || null) : null);
      }
    }
    return slots;
  };

  // Render day headers for a slot array
  const renderHeaders = (slots) => (
    <>
      <div />
      {slots.map((d, i) => {
        const wb = i > 0 && i % daysPerWeek === 0;
        return (
          <div key={i} className="sched-edit-grid-header" style={{ borderLeft: wb ? '2px solid #E5E7EB' : undefined, paddingLeft: wb ? 2 : 0 }}>
            {d ? (<><span className="sched-edit-cal-day">{d.day}</span><span className="sched-edit-cal-date">{d.month} {d.date}</span></>) : null}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="sched-edit-summary-wrap">
      {/* Overall title */}
      <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#003D4C', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #F3F4F6' }}>
        Staff Before / After View
      </div>

      {/* ═══ CALENDAR VIEW ═══ */}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Calendar View</div>

      {weekRows.map((rowWeeks, ri) => {
        const slots = buildSlots(rowWeeks);
        return (
          <div key={`cal-${ri}`} className="sched-edit-row" style={{ marginBottom: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: '0 2px', alignItems: 'center' }}>
              {renderHeaders(slots)}

              {/* Now row */}
              {!isNewEntry && <>
                <div className="sched-edit-grid-label">Now</div>
                {slots.map((d, i) => {
                  const wb = i > 0 && i % daysPerWeek === 0;
                  return <div key={i} className={`sched-edit-cal-cell sched-edit-cal-cell--now${d?.isRemoved ? ' sched-edit-cal-cell--removed' : ''}`}
                    style={{ borderLeft: wb ? '2px solid #E5E7EB' : undefined }}>{d ? (d.thisJobBefore > 0 ? r2(d.thisJobBefore) : '—') : ''}</div>;
                })}
              </>}

              {/* New row */}
              <div className="sched-edit-grid-label">New</div>
              {slots.map((d, i) => {
                const wb = i > 0 && i % daysPerWeek === 0;
                if (!d) return <div key={i} className="sched-edit-cal-cell" style={{ borderLeft: wb ? '2px solid #E5E7EB' : undefined }} />;
                return <div key={i} className={`sched-edit-cal-cell sched-edit-cal-cell--new${
                  d.hasPTO ? ' sched-edit-cal-cell--pto' : d.hasHoliday && d.thisJobAfter > 0 ? ' sched-edit-cal-cell--holiday' : d.hasGlobalAssignment && d.thisJobAfter > 0 ? ' sched-edit-cal-cell--global' : d.isNew ? ' sched-edit-cal-cell--added' :
                  d.isRemoved ? ' sched-edit-cal-cell--removed-new' : d.isModified ? ' sched-edit-cal-cell--modified' :
                  d.thisJobAfter > 0 ? ' sched-edit-cal-cell--unchanged' : ''
                }${d.thisJobAfter > d.cap && d.cap >= 0 ? ' sched-edit-cal-cell--over' : ''}`}
                  style={{ borderLeft: wb ? '2px solid #E5E7EB' : undefined }}
                  title={d.hasHoliday ? d.holidayName : undefined}>
                  {d.hasPTO ? <span style={{ fontSize: 14 }}>{'🏖️'}</span> : d.hasHoliday && d.thisJobAfter > 0 ? <span style={{ fontSize: 14 }}>{'🎄'}</span> : d.hasGlobalAssignment && d.thisJobAfter > 0 ? <span style={{ fontSize: 14 }}>{'📅'}</span> : d.thisJobAfter > 0 ? r2(d.thisJobAfter) : '—'}
                </div>;
              })}

              {/* Delta labels */}
              <div />
              {slots.map((d, i) => {
                const wb = i > 0 && i % daysPerWeek === 0;
                if (!d) return <div key={i} className="sched-edit-cal-label" style={{ borderLeft: wb ? '2px solid transparent' : undefined }} />;
                return <div key={i} className="sched-edit-cal-label" style={{
                  borderLeft: wb ? '2px solid transparent' : undefined,
                  color: d.hasPTO ? '#F59E0B' : d.hasHoliday && d.thisJobAfter > 0 ? '#7C3AED' : d.hasGlobalAssignment && d.thisJobAfter > 0 ? '#0891B2' : d.isNew ? '#F59E0B' : d.isRemoved ? '#9CA3AF' : d.delta > 0 ? '#059669' : d.delta < 0 ? '#DC2626' : '#D1D5DB',
                }}>{d.hasPTO ? 'PTO' : d.hasHoliday && d.thisJobAfter > 0 ? 'HOL' : d.hasGlobalAssignment && d.thisJobAfter > 0 ? 'EVENT' : d.isNew ? '+NEW' : d.isRemoved ? 'DEL' : Math.abs(d.delta) > 0.01 ? `${d.delta > 0 ? '+' : ''}${r2(d.delta)}` : ''}</div>;
              })}
            </div>
          </div>
        );
      })}

      {/* ═══ CAPACITY VIEW ═══ */}
      <div style={{ marginTop: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Capacity View</div>
        {!isNewEntry && <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 2 }}>Each day shows two bars: left is current hours, right is after proposed changes. Red indicates hours over capacity.</div>}
      </div>

      {weekRows.map((rowWeeks, ri) => {
        const slots = buildSlots(rowWeeks);
        return (
          <div key={`hist-${ri}`} className="sched-edit-row" style={{ marginBottom: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: '0 2px', alignItems: 'end' }}>
              {/* Day headers (same as calendar) */}
              <div />
              {slots.map((d, i) => {
                const wb = i > 0 && i % daysPerWeek === 0;
                return (
                  <div key={i} className="sched-edit-grid-header" style={{ borderLeft: wb ? '2px solid #E5E7EB' : undefined, paddingLeft: wb ? 2 : 0 }}>
                    {d ? (<><span className="sched-edit-cal-day">{d.day}</span><span className="sched-edit-cal-date">{d.month} {d.date}</span></>) : null}
                  </div>
                );
              })}

              {/* Histogram bars — two stacked columns per day: Before (left) | After (right) */}
              <div />
              {slots.map((d, i) => {
                const H = 85; // container height px
                const wb = i > 0 && i % daysPerWeek === 0;
                if (!d) return <div key={i} style={{ height: H, borderLeft: wb ? '2px solid #E5E7EB' : undefined }} />;

                const px = (v) => Math.max(0, (v / maxH) * H);
                const capPx = px(d.cap);
                const otherPx = px(d.otherJobHours);
                const globalPx = px(d.globalHours || 0);
                const origPx = px(d.thisJobBefore);
                const proposedPx = px(d.thisJobAfter);

                // Before stack: other + global + this-job (edited entry's orig + other same-job entries)
                const beforeTotal = d.otherJobHours + (d.globalHours || 0) + d.thisJobBefore;
                const beforeOverPx = beforeTotal > d.cap && d.cap > 0 ? px(beforeTotal - d.cap) : 0;
                const beforeOrigPx = Math.max(0, origPx - beforeOverPx);

                // After stack: other + global + this-job (proposed + other same-job entries)
                const afterTotal = d.otherJobHours + (d.globalHours || 0) + d.thisJobAfter;
                const afterOverPx = afterTotal > d.cap && d.cap > 0 ? px(afterTotal - d.cap) : 0;
                const afterProposedPx = Math.max(0, proposedPx - afterOverPx);

                return (
                  <div key={i} style={{ height: H, position: 'relative', borderLeft: wb ? '2px solid #E5E7EB' : undefined, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 3, padding: '0 1px' }}>
                    {/* Capacity line */}
                    {d.cap > 0 && <div style={{ position: 'absolute', left: 0, right: 0, bottom: capPx, borderTop: '1.5px dashed #9CA3AF', zIndex: 2 }} />}
                    {/* PTO zone */}
                    {d.hasPTO && <div className="sched-edit-hist-pto" />}

                    {/* LEFT: Before stack (other + global + this job) — 65% opacity */}
                    {!isNewEntry && (
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: 14, borderRadius: '2px 2px 0 0', overflow: 'hidden', opacity: 0.65 }}>
                        {beforeOverPx > 0 && <div style={{ height: beforeOverPx, background: '#EF4444' }} />}
                        {beforeOrigPx > 0 && <div style={{ height: Math.max(beforeOrigPx, 1), background: d.hasPTO ? '#F59E0B' : d.hasHoliday ? '#7C3AED' : '#059669' }} />}
                        {globalPx > 0 && <div style={{ height: globalPx, background: '#0891B2' }} />}
                        {otherPx > 0 && <div style={{ height: otherPx, background: '#9CA3AF' }} />}
                      </div>
                    )}

                    {/* RIGHT: After stack (other + global + this job) — full opacity */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: 14, borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                      {afterOverPx > 0 && <div style={{ height: afterOverPx, background: '#EF4444' }} />}
                      {afterProposedPx > 0 && <div style={{ height: Math.max(afterProposedPx, 1), background: d.hasPTO ? '#F59E0B' : d.hasHoliday ? '#7C3AED' : '#059669' }} />}
                      {globalPx > 0 && <div style={{ height: globalPx, background: '#0891B2' }} />}
                      {otherPx > 0 && <div style={{ height: otherPx, background: '#9CA3AF' }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6B7280', marginTop: 4, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 16, borderTop: '1.5px dashed #9CA3AF' }} /> capacity</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#059669', borderRadius: 2 }} /> this job</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#9CA3AF', borderRadius: 2 }} /> other jobs</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#EF4444', borderRadius: 2 }} /> over capacity</span>
        {dayAnalysis.some(d => d.hasPTO && d.proposed > 0) && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#F59E0B', borderRadius: 2 }} /> conflicts with PTO</span>
        )}
        {dayAnalysis.some(d => d.hasHoliday) && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#7C3AED', borderRadius: 2 }} /> holiday</span>
        )}
        {dayAnalysis.some(d => d.hasGlobalAssignment) && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#0891B2', borderRadius: 2 }} /> firm-wide event</span>
        )}
      </div>

      {/* ═══ CHANGE SUMMARY ═══ */}
      {summaryItems.length > 0 && (
        <div className="sched-edit-change-summary">
          {summaryItems.map((item, i) => (
            <div key={i} className="sched-edit-change-item">
              <span style={{ marginRight: 6 }}>{item.icon}</span>
              <span style={{ color: item.color, fontWeight: 600 }}>{item.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
